import React, { useState, useMemo } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Stack, Typography, Button, IconButton, Alert, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { SubscriptionData } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  products: any[];
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function AddTreatsModal({ open, onClose, sub, products, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [selections, setSelections] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const treatProducts = useMemo(() => {
    return products.filter((p: any) => {
      const tags = String(p.tags || "").toLowerCase();
      const type = String(p.product_type || "").toLowerCase();
      return tags.includes("treat") || type === "treat" || type === "treats";
    });
  }, [products]);

  const hasSelections = Object.values(selections).some(v => v > 0);

  const handleConfirm = async () => {
    if (!sub || !hasSelections) return;
    setLoading(true);
    setError(null);

    try {
      const treats = Object.entries(selections)
        .filter(([, qty]) => qty > 0)
        .map(([variantId, qty]) => ({ variantId, quantity: qty }));

      const res = await call("/recharge/subscription/add-treats", {
        method: "POST",
        body: { subscriptionId: sub.id, treats },
      });
      if (!res.ok) throw new Error(res.error || "Failed to add treats");
      onSuccess("Treats added successfully!");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to add treats");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800 }}>Add Treats</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {treatProducts.length === 0 ? (
          <Alert severity="warning">No treat products found.</Alert>
        ) : (
          <Stack spacing={2}>
            {treatProducts.map((product: any) => {
              const variant = product.variants?.[0];
              if (!variant) return null;
              const vid = String(variant.id);
              const count = selections[vid] || 0;

              return (
                <Box key={product.id} sx={{
                  display: "flex", alignItems: "center", gap: 2, p: 1.5, borderRadius: "8px",
                  bgcolor: count > 0 ? COLORS.greenLight : QUIZ.color.cream2,
                  border: count > 0 ? `2px solid ${COLORS.green}` : "1px solid #ddd",
                }}>
                  {(product.images?.[0]?.src || product.image?.src) && (
                    <Box component="img" src={product.images?.[0]?.src || product.image?.src} alt={product.title}
                      sx={{ width: 50, height: 50, borderRadius: "8px", objectFit: "cover" }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{product.title}</Typography>
                    <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>£{variant.price}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton size="small" onClick={() => setSelections(prev => ({ ...prev, [vid]: Math.max(0, (prev[vid] || 0) - 1) }))}><RemoveIcon /></IconButton>
                    <Typography sx={{ fontWeight: 700, minWidth: 24, textAlign: "center" }}>{count}</Typography>
                    <IconButton size="small" onClick={() => setSelections(prev => ({ ...prev, [vid]: (prev[vid] || 0) + 1 }))}><AddIcon /></IconButton>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || !hasSelections}
          sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Add Treats"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
